import { TransportNeedGraphPoint } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/types";
import {
  AreaChartMarkers,
  getDate,
  getValue,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/utils";
import { Semantics } from "shared/components/drops/DropsBadge";

export interface MarkerToRender {
  key: string;
  cx: number;
  cy: number;
  type?: string;
  variant: Semantics;
  passengers: number;
  date: Date;
}

const findClosestXPoint = (
  points: TransportNeedGraphPoint[],
  targetX: number,
  dateScale: (d: Date) => number,
): number => {
  if (!points.length) return targetX;
  let closestX = dateScale(getDate(points[0]));
  let minDistance = Math.abs(closestX - targetX);
  points.forEach((point) => {
    const x = dateScale(getDate(point));
    const distance = Math.abs(x - targetX);
    if (distance < minDistance) {
      minDistance = distance;
      closestX = x;
    }
  });

  return closestX;
};

interface BuildMarkersToRenderArgs {
  markers: AreaChartMarkers;
  dateScale: (d: Date) => number;
  valueScale: (v: number) => number;
  hoverX?: number | null;
  hoverData?: TransportNeedGraphPoint | null;
  points: TransportNeedGraphPoint[];
}

export const buildMarkersToRender = ({
  markers,
  dateScale,
  valueScale,
  hoverX,
  hoverData,
  points,
}: BuildMarkersToRenderArgs): MarkerToRender[] => {
  const items: MarkerToRender[] = [];

  let hoverXPoint: number | null = null;
  if (hoverX != null && hoverData) {
    const passengerCount = getValue(hoverData);
    const interpolatedPassengerCount = passengerCount;
    const cy = valueScale(interpolatedPassengerCount);
    const variant: Semantics = "neutral";

    // Find closest existing marker to place a point closest to cursor x where theres data
    hoverXPoint = findClosestXPoint(points, hoverX, dateScale);
    items.push({
      key: `hover-${hoverData.date.getTime()}-${hoverData.passengers}`,
      cx: hoverXPoint,
      cy,
      variant,
      passengers: hoverData.passengers,
      date: hoverData.date,
    });
  }

  if (
    markers.currentTimeMarker &&
    hoverXPoint !== dateScale(getDate(markers.currentTimeMarker)) // Avoid duplicate marker
  ) {
    const m = markers.currentTimeMarker;
    const variant: Semantics = "info";
    items.push({
      key: `current-${m.date.getTime()}-${m.passengers}`,
      cx: dateScale(getDate(m)),
      cy: valueScale(getValue(m)),
      type: "Siste",
      variant,
      passengers: m.passengers,
      date: m.date,
    });
  }

  if (
    markers.maxPassengersMarker &&
    hoverXPoint !== dateScale(getDate(markers.maxPassengersMarker)) &&
    markers.maxPassengersMarker !== markers.currentTimeMarker
  ) {
    const m = markers.maxPassengersMarker;
    const variant: Semantics = "alarm";
    items.push({
      key: `max-${m.date.getTime()}-${m.passengers}`,
      cx: dateScale(getDate(m)),
      cy: valueScale(getValue(m)),
      type: "Maks",
      variant,
      passengers: m.passengers,
      date: m.date,
    });
  }

  return items;
};
